import React, { useState } from "react";
import { ChevronDown, ChevronRight } from "lucide-react";
import { Ticker, pct, tone, mcap } from "./UniverseBacktestParts";

const CAUGHT = {
  before: { label: "Alerted before the report", cls: "bg-emerald-500/15 text-emerald-600" },
  after: { label: "Alerted only after", cls: "bg-amber-500/15 text-amber-600" },
  no: { label: "Not alerted", cls: "bg-red-500/15 text-red-600" },
};

export function CaughtBadge({ caught }) {
  const c = CAUGHT[caught];
  return <span className={`inline-block rounded px-2 py-0.5 text-[11px] font-semibold ${c.cls}`}>{c.label}</span>;
}

export function SpcxCase({ ev, note }) {
  if (!ev) {
    return <p className="text-sm text-neutral-600" data-testid="uxbt-spcx-none">No SPCX earnings report with a 7%+ move fell inside the window. {note}</p>;
  }
  return (
    <div data-testid="uxbt-spcx-case">
      <div className="flex flex-wrap items-center gap-3">
        <Ticker sym="SPCX" />
        <CaughtBadge caught={ev.caught} />
        <span className={`font-mono-data font-semibold ${tone(ev.move_pct)}`}>{pct(ev.move_pct, 2)} on earnings</span>
      </div>
      <dl className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-3 text-sm">
        <div><dt className="text-xs text-neutral-500">Report</dt><dd className="text-mb-ink">{ev.report_date} · {ev.timing}{ev.reaction_partial ? " · reaction session still trading (partial bar)" : ""}</dd></div>
        <div><dt className="text-xs text-neutral-500">Pre-report price</dt><dd className="font-mono-data">${ev.pre_price} · {mcap(ev.market_cap)}</dd></div>
        <div><dt className="text-xs text-neutral-500">Highest score, 5 sessions before</dt><dd className="font-mono-data" data-testid="uxbt-spcx-score">{ev.best_score}/3 signals · {ev.best_confidence ? `${Math.round(ev.best_confidence * 100)}% confidence` : "never passed the screen"}</dd></div>
        <div><dt className="text-xs text-neutral-500">Why it was or wasn't alerted</dt><dd className="text-mb-ink" data-testid="uxbt-spcx-reason">{ev.caught === "before" ? `Published ${ev.alert_date}` : ev.blocked_by_label}{ev.caught === "after" ? ` — then alerted ${ev.alert_date}` : ""}</dd></div>
        <div><dt className="text-xs text-neutral-500">V11 return from the pre-report close</dt><dd className={`font-mono-data ${tone(ev.hypo_v11_return_pct)}`}>{pct(ev.hypo_v11_return_pct, 2)} ({ev.hypo_v11_status.replace("_", " ")})</dd></div>
      </dl>
      <div className="mt-4 text-xs text-neutral-500">
        Day by day: {Object.entries(ev.day_reasons).map(([d, r]) => `${d.slice(5)} ${r.replaceAll("_", " ")}`).join(" · ")}
      </div>
      {note && <p className="mt-2 text-[11px] italic text-neutral-500">{note}</p>}
    </div>
  );
}

export function EarningsTable({ rows }) {
  const [open, setOpen] = useState(false);
  if (!rows.length) return <p className="text-sm text-neutral-500">No qualifying earnings moves in the window.</p>;
  const lead = rows.filter((e) => e.symbol === "SPCX").length + 12;
  const shown = open ? rows : rows.slice(0, lead);
  const noneCaught = rows.every((e) => e.caught === "no");
  return (
    <div>
      {noneCaught && (
        <p className="mb-3 text-xs text-neutral-600" data-testid="uxbt-earnings-none-note">
          None of these {rows.length} reports was alerted, before or after. The "Stopped by" column shows the rule that blocked each one.
        </p>
      )}
    <div className="overflow-x-auto">
      <table className="w-full text-sm min-w-[760px]" data-testid="uxbt-earnings-table">
        <thead>
          <tr className="text-left text-xs uppercase tracking-wide text-neutral-500">
            <th className="py-1.5 pr-3 font-medium">Ticker</th><th className="py-1.5 pr-3 font-medium">Report</th>
            <th className="py-1.5 pr-3 font-medium text-right">Move</th><th className="py-1.5 pr-3 font-medium text-right">Best score</th>
            <th className="py-1.5 pr-3 font-medium">Result</th><th className="py-1.5 pr-3 font-medium">Stopped by</th>
            <th className="py-1.5 font-medium text-right">V11 return</th>
          </tr>
        </thead>
        <tbody>
          {shown.map((e) => (
            <tr key={`${e.symbol}-${e.report_date}`} className="border-t border-mb-border" data-testid={`uxbt-earn-${e.symbol}-${e.report_date}`}>
              <td className="py-1.5 pr-3"><Ticker sym={e.symbol} /></td>
              <td className="py-1.5 pr-3 font-mono-data text-neutral-700">{e.report_date}</td>
              <td className={`py-1.5 pr-3 text-right font-mono-data ${tone(e.move_pct)}`}>{pct(e.move_pct)}</td>
              <td className="py-1.5 pr-3 text-right font-mono-data">{e.best_score}/3{e.best_confidence ? ` · ${Math.round(e.best_confidence * 100)}%` : ""}</td>
              <td className="py-1.5 pr-3"><CaughtBadge caught={e.caught} />{e.alert_date ? <span className="ml-1 text-[11px] text-neutral-500">{e.alert_date}</span> : null}</td>
              <td className="py-1.5 pr-3 text-xs text-neutral-600">{e.blocked_by_label || "—"}</td>
              <td className={`py-1.5 text-right font-mono-data ${tone(e.alert_return_pct ?? e.hypo_v11_return_pct)}`}>{pct(e.alert_return_pct ?? e.hypo_v11_return_pct, 2)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
      {rows.length > lead && (
        <button onClick={() => setOpen(!open)} className="mt-3 text-xs font-semibold text-mb-violet inline-flex items-center gap-1" data-testid="uxbt-earnings-toggle">
          {open ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
          {open ? "Show fewer" : `Show all ${rows.length}`}
        </button>
      )}
    </div>
  );
}
